import { useMemo } from "react";
import { buildRevenueEpsHistory } from "../../lib/incomeStatementGraph";
import IncomeStatementWaterfall from "../charts/IncomeStatementWaterfall";

// QoQ / YoY chips + waterfall income-statement chart, mirroring the
// "Key Metrics" + waterfall slides from earnings-shorts.

function fmtPct(v) {
  if (v == null || isNaN(v)) return 'N/A';
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

function fmtRevenue(v) {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  return v.toFixed(0);
}

function fmtEps(v) {
  if (v == null || isNaN(v)) return '—';
  return `$${v.toFixed(2)}`;
}

function pctTone(v) {
  if (v == null) return { text: 'text-gray-400', bg: 'bg-gray-800/60 border-gray-700/50' };
  if (v >= 0)     return { text: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30' };
  return                  { text: 'text-red-400',   bg: 'bg-red-500/10 border-red-500/30' };
}

function Kpi({ label, pct, sub }) {
  const tone = pctTone(pct);
  return (
    <div className={`rounded-lg border px-3 py-3 text-center ${tone.bg}`}>
      <div className="text-gray-400 text-[10px] uppercase tracking-wide">{label}</div>
      <div className={`mt-1 font-bold text-lg ${tone.text}`}>{fmtPct(pct)}</div>
      <div className="text-gray-500 text-[10px] mt-0.5 truncate">{sub}</div>
    </div>
  );
}

export default function LatestEarnings({ data, sankeyMapData, isQuarterly = true }) {
  const history = useMemo(() => buildRevenueEpsHistory(data || []), [data]);

  if (!data || data.length === 0) {
    return (
      <section className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-white tracking-wide mb-3">Latest Earnings</h3>
        <p className="text-sm text-gray-400 py-6 text-center">No earnings data</p>
      </section>
    );
  }

  // cur = latest period, prev = previous chronological period (QoQ),
  // yoy = same fp one year earlier (YoY).
  const cur  = history[history.length - 1];
  const prev = history[history.length - 2];
  // fy comes from the API as a string ("2024"), so compare as numbers.
  const yoy = cur ? history.find(p => p.fp === cur.fp && Number(p.fy) === Number(cur.fy) - 1) : null;

  const revQoQ = (prev?.revenue != null && cur?.revenue != null && prev.revenue !== 0)
    ? (cur.revenue - prev.revenue) / Math.abs(prev.revenue) * 100 : null;
  const revYoY = (yoy?.revenue  != null && cur?.revenue != null && yoy.revenue  !== 0)
    ? (cur.revenue - yoy.revenue)  / Math.abs(yoy.revenue)  * 100 : null;

  const curEPS  = cur?.eps_diluted  ?? cur?.eps_basic  ?? null;
  const prevEPS = prev?.eps_diluted ?? prev?.eps_basic ?? null;
  const yoyEPS  = yoy?.eps_diluted  ?? yoy?.eps_basic  ?? null;

  const epsQoQ = (prevEPS != null && curEPS != null && prevEPS !== 0)
    ? (curEPS - prevEPS) / Math.abs(prevEPS) * 100 : null;
  const epsYoY = (yoyEPS  != null && curEPS != null && yoyEPS  !== 0)
    ? (curEPS - yoyEPS)  / Math.abs(yoyEPS)  * 100 : null;

  return (
    <section className="space-y-3">
      <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white tracking-wide">Latest Earnings</h3>
          {cur && (
            <div className="px-3 py-1 rounded-md border border-blue-500/40 bg-blue-500/10 text-right">
              <div className="text-blue-400 font-bold text-xs">{cur.label}</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Kpi
            label="Revenue QoQ"
            pct={revQoQ}
            sub={`${fmtRevenue(prev?.revenue)} → ${fmtRevenue(cur?.revenue)}`}
          />
          <Kpi
            label="Revenue YoY"
            pct={revYoY}
            sub={yoy ? `vs ${yoy.label}` : 'no prior year'}
          />
          <Kpi
            label="EPS QoQ"
            pct={epsQoQ}
            sub={`${fmtEps(prevEPS)} → ${fmtEps(curEPS)}`}
          />
          <Kpi
            label="EPS YoY"
            pct={epsYoY}
            sub={yoy ? `vs ${yoy.label}` : 'no prior year'}
          />
        </div>
      </div>

      {/* Waterfall income-statement chart for the same latest period. */}
      <IncomeStatementWaterfall
        data={data}
        sankeyMapData={sankeyMapData}
        isQuarterly={isQuarterly}
      />
    </section>
  );
}
